// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Utilities for serializing and deserializing inventory filters to/from query strings.
 * This module provides functions to convert InventoryFilters objects to URL-safe strings
 * and back, enabling shareable and bookmarkable filtered inventory views.
 */
import { InventoryFilters } from '@/inventory/api/types';

/**
 * Type-safe keys for array-based inventory filters
 * This type needs to be filtered to create a list of array filters vs bool filters
 */
type InventoryArrayFilterKeys = keyof Pick<
  InventoryFilters,
  'itemKeys' | 'locationIds' | 'productKeys' | 'assetIds' | 'assetKeys'
>;

/**
 * Type-safe keys for boolean-based inventory filters
 */
type InventoryBooleanFilterKeys = keyof Pick<
  InventoryFilters,
  | 'includeNonInventory'
  | 'includeInTransit'
  | 'includeScrappedAssets'
  | 'includeArchivedProducts'
  | 'excludeArchivedLocations'
  | 'excludeSerials'
>;

/**
 * All valid inventory filter keys
 */
type InventoryFilterKeys =
  | InventoryArrayFilterKeys
  | InventoryBooleanFilterKeys;

/**
 * Type guard to check if a key is a boolean filter key
 */
function isBooleanFilterKey(key: string): key is InventoryBooleanFilterKeys {
  return [
    'includeNonInventory',
    'includeInTransit',
    'includeScrappedAssets',
    'includeArchivedProducts',
    'excludeArchivedLocations',
    'excludeSerials',
  ].includes(key as InventoryBooleanFilterKeys);
}

/**
 * Type guard to check if a key is an array filter key
 */
function isArrayFilterKey(key: string): key is InventoryArrayFilterKeys {
  return [
    'itemKeys',
    'locationIds',
    'productKeys',
    'assetIds',
    'assetKeys',
  ].includes(key as InventoryArrayFilterKeys);
}

/**
 * Serializes inventory filters to a query string key.
 * Converts the filters object to a stable, URL-safe string that can be used as a query key.
 * Empty or undefined filters are normalized to ensure consistent caching.
 *
 * @param filters - The inventory filters object to serialize
 * @returns A stable string representation of the filters for use in query keys
 *
 * @example
 * ```typescript
 * const filters = { itemKeys: ['part1', 'part2'], locationIds: ['loc1'] };
 * const key = serializeFiltersToKey(filters);
 * // Returns: "itemKeys:part1,part2|locationIds:loc1"
 * ```
 */
export function serializeFiltersToKey(filters?: InventoryFilters): string {
  if (!filters) {
    return '';
  }

  const parts: string[] = [];

  // Sort the keys to ensure consistent serialization
  const sortedKeys = Object.keys(filters).sort() as InventoryFilterKeys[];

  for (const key of sortedKeys) {
    const value = filters[key];

    if (typeof value === 'boolean' && isBooleanFilterKey(key)) {
      parts.push(`${key}:${value.toString()}`);
    } else if (
      Array.isArray(value) &&
      value.length > 0 &&
      isArrayFilterKey(key)
    ) {
      // Sort the array values for consistency
      const sortedValues = [...value].sort();
      parts.push(`${key}:${sortedValues.join(',')}`);
    }
  }

  return parts.join('|');
}

/**
 * Creates a normalized filters object for consistent query key generation.
 * Removes empty arrays and undefined values to ensure the same logical filters
 * produce the same query key regardless of how they're represented.
 *
 * @param filters - The inventory filters object to normalize
 * @returns A normalized filters object with empty values removed
 *
 * @example
 * ```typescript
 * const filters = { itemKeys: ['part1'], locationIds: [], productKeys: undefined };
 * const normalized = normalizeFilters(filters);
 * // Returns: { itemKeys: ['part1'] }
 * ```
 */
export function normalizeFilters(filters?: InventoryFilters): InventoryFilters {
  if (!filters) {
    return {};
  }

  const normalized: InventoryFilters = {};

  for (const [key, value] of Object.entries(filters)) {
    if (typeof value === 'boolean' && isBooleanFilterKey(key)) {
      normalized[key] = value;
    } else if (
      Array.isArray(value) &&
      value.length > 0 &&
      isArrayFilterKey(key)
    ) {
      normalized[key] = value;
    }
  }

  return normalized;
}
